import React, { useState, useMemo, useEffect } from "react"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  FormGroup,
  Label,
  Input,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import { FaClock, FaMapMarkerAlt, FaUsers } from "react-icons/fa"
import { Calendar, momentLocalizer } from "react-big-calendar"
import moment from "moment"
import "react-big-calendar/lib/css/react-big-calendar.css"
import WelcomeComp from "./WelcomeComp"
import { BsClipboardCheckFill, BsClipboardMinusFill } from "react-icons/bs"
import Select from "react-select"
import { FaLocationPin } from "react-icons/fa6"
import { MdLocationOff } from "react-icons/md"
import { fetchData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import { formatAnyTime } from "utils/commonFunctions"

const Dashboard = props => {
  document.title = "Dashboard | J. Kyle Cleaning Services"
  const localizer = momentLocalizer(moment)

  const [data, setData] = useState({})
  const [calenderData, setCalenderData] = useState([])
  const [loading, setLoading] = useState(false)

  const today = new Date()

  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1)
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [calView, setCalView] = useState("month")

  const tomorrow = new Date()
  tomorrow.setDate(today.getDate() + 1)

  const [selectedDate, setSelectedDate] = useState(today)
  const [locationFilter, setLocationFilter] = useState("")
  const [employeeFilter, setEmployeeFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [locationTypeFilter, setLocationTypeFilter] = useState("")

  // Sample events data
  const [allData, setAllData] = useState([])
  const [events, setEvents] = useState([])
  const [timesheets, settimesheets] = useState([])

  useEffect(() => {
    getData()
  }, [selectedDate])

  useEffect(() => {
    getData2()
  }, [currentMonth, currentYear])

  async function getData() {
    const APIdata = await fetchData(
      setLoading,
      `dashboard/counts?date=${moment(selectedDate)?.format("YYYY-MM-DD")}`
    )
    if (APIdata?.success) {
      setData(APIdata?.data)
      setAllData(APIdata?.events)
      setEvents(APIdata?.events)
      settimesheets(APIdata?.timesheets)
    } else {
      setData({})
      settimesheets([])
    }
  }

  async function getData2() {
    const APIdata2 = await fetchData(setLoading, "calendar")
    if (APIdata2?.success) {
      const events = APIdata2.data
      const groupedByDate = events.reduce((acc, ev) => {
        const d = ev.date
        if (!acc[d]) {
          acc[d] = { locations: new Set(), employees: new Set() }
        }
        acc[d].locations.add(ev.locationId)
        acc[d].employees.add(ev.employeeId)
        return acc
      }, {})

      const computedData = Object.entries(groupedByDate).map(
        ([date, counts]) => ({
          date,
          locations: counts.locations.size,
          employees: counts.employees.size,
          start: new Date(date + "T00:00:00"),
          end: new Date(date + "T23:59:59"),
        })
      )

      setCalenderData(computedData)
    } else {
      setCalenderData([])
    }
  }

  const uniqueLocations = [
    ...new Map(
      allData?.map(e => [
        e.locationId,
        { id: e.locationId, name: e.location?.name },
      ])
    ).values(),
  ]

  const uniqueEmployees = [
    ...new Map(
      allData?.map(e => [
        e.employeeId,
        { id: e.employeeId, name: e.employee?.name },
      ])
    ).values(),
  ]

  // Filter events based on selected date and dropdowns
  const selectedDateEvents = useMemo(() => {
    const selectedDateStr = moment(selectedDate).format("YYYY-MM-DD")

    return events.filter(event => {
      // 1 Match date
      const dateMatch = event.date === selectedDateStr

      // 2 Match location
      const locationMatch =
        !locationFilter || event.locationId === Number(locationFilter)

      // 3 Match employee
      const employeeMatch =
        !employeeFilter || event.employeeId === Number(employeeFilter)

      // 4 Match cleaning type (same logic as Calendar)
      const matchType =
        !locationTypeFilter?.length ||
        JSON.parse(event.location?.type || "[]").some(t =>
          locationTypeFilter.includes(t)
        )

      // 5 Find if timesheet exists (Completed or Pending)
      const relatedSheet = timesheets?.find(
        ts => ts.calenderSlotId === event.id
      )

      // Status based on timesheet
      const eventStatus = relatedSheet ? "Completed" : "Pending"

      // 6 Match status
      const statusMatch = !statusFilter || eventStatus === statusFilter

      return dateMatch && locationMatch && employeeMatch && matchType && statusMatch
    })
  }, [
    events,
    selectedDate,
    locationFilter,
    employeeFilter,
    locationTypeFilter,
    statusFilter,
    timesheets,
  ])

  const eventStyleGetter = event => ({ style: { display: "none" } })

  const dateCellWrapper = ({ value, children }) => {
    const dateKey = moment(value).format("YYYY-MM-DD")
    const eventsForDate = calenderData.find(ev => ev.date === dateKey)

    const isSelected =
      selectedDate &&
      moment(value).format("YYYY-MM-DD") ===
        moment(selectedDate).format("YYYY-MM-DD")

    return (
      <div
        onClick={() => setSelectedDate(value)}
        style={{
          height: "100%",
          width: "100%",
          cursor: "pointer",
          padding: "5px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "14px",
          borderRadius: "6px",
          backgroundColor: isSelected ? "#e1f5fe" : "#fff",
          border: "1px solid #eee",
        }}
      >
        <>
          <div
            className="d-flex align-items-center"
            title={`${eventsForDate?.locations || 0} locations`}
          >
            <i className="fas fa-map-marker-alt text-danger fs-5" />
            <span className="fs-6 fw-semibold ms-2">
              {eventsForDate?.locations || 0}
            </span>
          </div>
          <div
            className="d-flex align-items-center"
            title={`${eventsForDate?.employees || 0} employees`}
          >
            <i className="fas fa-users text-primary fs-5" />
            <span className="fs-6 fw-semibold ms-2">
              {eventsForDate?.employees || 0}
            </span>
          </div>
        </>
      </div>
    )
  }

  const dayPropGetter = date => {
    const isSelected =
      selectedDate &&
      moment(date).format("YYYY-MM-DD") ===
        moment(selectedDate).format("YYYY-MM-DD")
    return {
      style: {
        backgroundColor: isSelected ? "#e1f5fe" : "white",
        border: "1px solid #eee",
        height: "100px",
        padding: "0",
      },
    }
  }

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Dashboards")}
            breadcrumbItem={props.t("Dashboard")}
            description={"Showing the Datewise Assigned Location and Jobs"}
          />

          {/* Top stats cards */}
          <Row className="px-0">
            <Col xl={4} lg={4} md={12}>
              <WelcomeComp />
            </Col>
            <Col xl="8" lg="8" md="12">
              <Row xl={12}>
                {/* Total Employees */}
                <Col md="4">
                  <Card className="mini-stats-wid mb-3 shadow-lg">
                    <CardBody className="d-flex align-items-center justify-content-between p-3">
                      <div>
                        <h3 className="mb-2 fw-bold">{data?.employees}</h3>
                        <h6>Total Employees</h6>
                      </div>
                      <div className="avatar-sm rounded-circle mini-stat-icon">
                        <span className="avatar-title rounded-circle">
                          <FaUsers fontSize={24} />
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Completed Locations */}
                <Col md="4">
                  <Card className="mini-stats-wid mb-3 shadow-lg">
                    <CardBody className="d-flex align-items-center justify-content-between p-3">
                      <div>
                        <h3 className="mb-2 fw-bold">
                          {data?.completedLocation}
                        </h3>
                        <h6>Completed Locations</h6>
                      </div>
                      <div className="avatar-sm rounded-circle mini-stat-icon">
                        <span className="avatar-title rounded-circle">
                          <FaMapMarkerAlt fontSize={24} />
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Remaining Locations */}
                <Col md="4">
                  <Card className="mini-stats-wid mb-3 shadow-lg">
                    <CardBody className="d-flex align-items-center justify-content-between p-3">
                      <div>
                        <h3 className="mb-2 fw-bold">
                          {data?.remainingLocation}
                        </h3>
                        <h6>Remaining Locations</h6>
                      </div>
                      <div className="avatar-sm rounded-circle mini-stat-icon">
                        <span className="avatar-title rounded-circle">
                          <MdLocationOff fontSize={24} />
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Today's Completed Services */}
                <Col md="4">
                  <Card className="mini-stats-wid mb-3 shadow-lg">
                    <CardBody className="d-flex align-items-center justify-content-between p-3">
                      <div>
                        <h3 className="mb-2 fw-bold">
                          {data?.completedServices}
                        </h3>
                        <h6>Completed Services</h6>
                      </div>
                      <div className="avatar-sm rounded-circle mini-stat-icon">
                        <span className="avatar-title rounded-circle">
                          <BsClipboardCheckFill fontSize={24} />
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Today's Remaining Services */}
                <Col md="4">
                  <Card className="mini-stats-wid mb-3 shadow-lg">
                    <CardBody className="d-flex align-items-center justify-content-between p-3">
                      <div>
                        <h3 className="mb-2 fw-bold">
                          {data?.remainingServices}
                        </h3>
                        <h6>Remaining Services</h6>
                      </div>
                      <div className="avatar-sm rounded-circle mini-stat-icon">
                        <span className="avatar-title rounded-circle">
                          <BsClipboardMinusFill fontSize={24} />
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Total Hrs */}
                <Col md="4">
                  <Card className="mini-stats-wid mb-3 shadow-lg">
                    <CardBody className="d-flex align-items-center justify-content-between p-3">
                      <div>
                        <h3 className="mb-2 fw-bold">{data?.totalHours}</h3>
                        <h6>Total Hours</h6>
                      </div>
                      <div className="avatar-sm rounded-circle mini-stat-icon">
                        <span className="avatar-title rounded-circle">
                          <FaClock fontSize={24} />
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Calendar + job list */}
          <Row className="mt-2">
            <Col md="6" className="">
              <Card className="shadow-lg">
                <CardBody>
                  <Calendar
                    localizer={localizer}
                    events={calenderData}
                    startAccessor="start"
                    endAccessor="end"
                    view={calView}
                    onView={v => setCalView(v)}
                    views={["month"]}
                    style={{ height: 500 }}
                    selectable
                    onSelectSlot={slotInfo => setSelectedDate(slotInfo.start)}
                    dayPropGetter={dayPropGetter}
                    components={{ dateCellWrapper }}
                    eventPropGetter={eventStyleGetter}
                    onDrillDown={() => {}}
                    onRangeChange={range => {
                      console.log("Range received:", range)

                      if (Array.isArray(range) && range.length > 0) {
                        // The FIRST date is always inside the month being shown
                        const anyDate = range.find(d => d.getMonth) ?? range[0]

                        setCurrentMonth(anyDate.getMonth() + 1) // +1 because JS months are 0-based
                        setCurrentYear(anyDate.getFullYear())
                      }
                    }}
                  />
                </CardBody>
              </Card>
            </Col>

            <Col md="6" className="mb-0">
              <Card className="shadow-lg">
                <CardBody className="">
                  <h5 className="text-center border-bottom pb-3 text-blue">
                    Locations on {moment(selectedDate).format("DD/MM/YYYY")}
                  </h5>
                  <Row className="mb-3 flex-wrap">
                    <Col md="3" sm="6" xs="12">
                      <FormGroup>
                        <Label for="locationTypeFilter">Cleaning Type</Label>
                        <Input
                          type="select"
                          id="locationTypeFilter"
                          value={locationTypeFilter}
                          onChange={e => setLocationTypeFilter(e.target.value)}
                          className="form-select"
                        >
                          <option value="">All (Both Cleaning Type)</option>
                          <option value="window_cleaning">Window Cleaning</option>
                          <option value="industrial_cleaning">Industrial Cleaning</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3" sm="6" xs="12">
                      <FormGroup>
                        <Label for="locationFilter">Location</Label>
                        <Input
                          type="select"
                          id="locationFilter"
                          value={locationFilter}
                          onChange={e => setLocationFilter(e.target.value)}
                          className="form-select"
                        >
                          <option value="">All Locations</option>
                          {uniqueLocations.map(loc => (
                            <option key={loc?.id} value={loc?.id}>
                              {loc?.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3" sm="6" xs="12">
                      <FormGroup>
                        <Label for="employeeFilter">Employee</Label>
                        <Input
                          type="select"
                          id="employeeFilter"
                          value={employeeFilter}
                          onChange={e => setEmployeeFilter(e.target.value)}
                          className="form-select"
                        >
                          <option value="">All Employees</option>
                          {uniqueEmployees.map(loc => (
                            <option key={loc?.id} value={loc?.id}>
                              {loc?.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3" sm="6" xs="12">
                      <FormGroup>
                        <Label for="statusFilter">Status</Label>
                        <Input
                          type="select"
                          id="statusFilter"
                          value={statusFilter}
                          onChange={e => setStatusFilter(e.target.value)}
                          className="form-select"
                        >
                          <option value="">All Statuses</option>
                          <option value="Completed">Completed</option>
                          <option value="Pending">Pending</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div
                    style={{
                      height: "450px",
                      overflowY: "scroll",
                      scrollbarWidth: "thin",
                    }}
                  >
                    <Table bordered responsive>
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th>Employee</th>
                          <th>Time</th>
                          <th>Completed Hours</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDateEvents.length > 0 ? (
                          selectedDateEvents.map(job => {
                            const RelatedSheet = timesheets?.find(
                              item => item?.calenderSlotId === job?.id
                            )
                            return (
                              <tr key={job?.id}>
                                <td className="text-wrap-safe">{job.location?.name}</td>
                                <td className="text-wrap-safe">{job.employee?.name}</td>
                                <td>
                                  {RelatedSheet?.inTime
                                    ? formatAnyTime(RelatedSheet?.inTime)
                                    : formatAnyTime(job?.inTime)}{" "}
                                  -{" "}
                                  {RelatedSheet?.outTime
                                    ? formatAnyTime(RelatedSheet?.outTime)
                                    : formatAnyTime(job?.outTime)}
                                </td>
                                <td>
                                  {RelatedSheet?.inTime && RelatedSheet?.outTime
                                    ? (() => {
                                        const inM = moment(
                                          RelatedSheet.inTime,
                                          ["HH:mm", "hh:mm A"]
                                        )
                                        const outM = moment(
                                          RelatedSheet.outTime,
                                          ["HH:mm", "hh:mm A"]
                                        )
                                        const diff = moment
                                          .duration(outM.diff(inM))
                                          .asMinutes()
                                        const h = Math.floor(
                                          Math.abs(diff) / 60
                                        )
                                        const m = Math.abs(diff) % 60
                                        return `${h} hrs ${m} min`
                                      })()
                                    : ""}
                                </td>
                                <td>
                                  {RelatedSheet ? (
                                    <span className="bedge bg-success p-1 text-white fw-bold rounded-4 px-3 fs-6">
                                      Completed
                                    </span>
                                  ) : (
                                    <span className="bedge bg-danger p-1 text-white fw-bold rounded-4 px-3 fs-6">
                                      Pending
                                    </span>
                                  )}
                                </td>
                              </tr>
                            )
                          })
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-center">
                              No jobs on this date
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(Dashboard)
